import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useT } from '../../i18n';

// Top-level switch between the views of the shared portfolio.
//
// NavLink rather than a click handler so each view keeps its own URL — they are
// separate pages, not a toggle, and a link to one of them should open it.
//
// The path is `/holdings/kanosei-index`, not `/holdings/index`: static servers
// treat a trailing `/index` as a directory index and redirect it away. It
// happens to survive on CloudFront, whose 404 rewrite catches it, but a route
// that only works because of an error handler is a route waiting to break.
//
// Order runs from the whole to the particular: the total portfolio, then its
// four sections. Notes, Transactions and Settings are bookkeeping rather than
// views of the book, so they sit together under Admin at the far right.

const ADMIN = [
    ['/holdings/notes', 'nav.notes'],
    ['/holdings/transactions', 'nav.transactions'],
    ['/holdings/settings', 'nav.settings'],
];

function AdminMenu() {
    const { t } = useT();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const active = ADMIN.some(([path]) => pathname.startsWith(path));

    useEffect(() => { setOpen(false); }, [pathname]);

    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
        const escape = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', escape);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', escape);
        };
    }, [open]);

    return (
        <div className="ptabs__admin" ref={ref}>
            <button
                type="button"
                className={`ptabs__tab ptabs__admin_btn${active ? ' is-active' : ''}`}
                aria-expanded={open}
                aria-haspopup="true"
                onClick={() => setOpen((v) => !v)}
            >
                {t('nav.admin')} <span aria-hidden="true">▾</span>
            </button>
            {open && (
                <div className="ptabs__menu">
                    {ADMIN.map(([path, label]) => (
                        <NavLink
                            key={path}
                            to={path}
                            className={({ isActive }) => `ptabs__menu_item${isActive ? ' is-active' : ''}`}
                        >
                            {t(label)}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function PortfolioTabs() {
    const { t } = useT();

    const tab = ({ isActive }) => `ptabs__tab${isActive ? ' is-active' : ''}`;

    return (
        <nav className="ptabs" aria-label={t('lang.viewsAria')}>
            <NavLink to="/holdings" end className={tab}>
                {t('nav.totalPortfolio')}
            </NavLink>
            <NavLink to="/holdings/kanosei-index" className={tab}>
                {t('nav.kanoseiIndex')}
            </NavLink>
            <NavLink to="/holdings/stocks" className={tab}>
                {t('nav.stockInvestments')}
            </NavLink>
            <NavLink to="/holdings/crypto" className={tab}>
                {t('nav.cryptoHoldings')}
            </NavLink>
            <NavLink to="/holdings/private" className={tab}>
                {t('nav.privateInvestments')}
            </NavLink>
            <AdminMenu />
        </nav>
    );
}
